import { api } from "../utils/api";
import type {
  RestBillingItem,
  RestCreateBillingItem,
  RestUpdateBillingItem,
} from "@KassaroAB/shared/types/billingItem";
import type {
  StoreId,
  BillingItemId,
  AvailableBillingItemId,
} from "@KassaroAB/shared/types/ids";
import type { Features } from "@KassaroAB/shared/types/enums/Features";
import type { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import type { Money } from "@KassaroAB/shared/types/money";

export interface SubscribeRequest {
  availableBillingItemId: AvailableBillingItemId;
  quantity: number;
  startDate: string;
  billingPeriod?: BillingPeriod | null;
  discount?: Money | null;
  isDemo?: boolean;
}

export interface SetQuantityRequest {
  availableBillingItemId: AvailableBillingItemId;
  quantity: number;
  effectiveFrom: string;
}

export interface CancelBillingItemRequest {
  availableBillingItemId: AvailableBillingItemId;
  effectiveFrom: string;
}

export interface SwitchPackageRequest {
  toAvailableBillingItemId: AvailableBillingItemId;
  effectiveFrom: string;
  billingPeriod?: BillingPeriod | null;
}

export const subscribeBillingItemApi = async (
  storeId: StoreId,
  request: SubscribeRequest
): Promise<RestBillingItem> => {
  return api.post(`stores/${storeId}/billingItems/subscribe`, request);
};

export const setBillingItemQuantityApi = async (
  storeId: StoreId,
  request: SetQuantityRequest
): Promise<RestBillingItem[]> => {
  return api.post(`stores/${storeId}/billingItems/setQuantity`, request);
};

export const cancelBillingItemApi = async (
  storeId: StoreId,
  request: CancelBillingItemRequest
): Promise<RestBillingItem[]> => {
  return api.post(`stores/${storeId}/billingItems/cancel`, request);
};

export const switchPackageApi = async (
  storeId: StoreId,
  request: SwitchPackageRequest
): Promise<RestBillingItem[]> => {
  return api.post(`stores/${storeId}/billingItems/switchPackage`, request);
};

export const getBillingItemsApi = async (
  storeId: StoreId,
): Promise<RestBillingItem[]> => {
  return api.get(`stores/${storeId}/billingItems`);
};

export const getBillingItemApi = async (
  storeId: StoreId,
  billingItemId: BillingItemId,
): Promise<RestBillingItem> => {
  return api.get(`stores/${storeId}/billingItems/${billingItemId}`);
};

export const createBillingItemApi = async (
  storeId: StoreId,
  billingItem: RestCreateBillingItem,
): Promise<RestBillingItem> => {
  return api.post(`stores/${storeId}/billingItems`, billingItem);
};

export const updateBillingItemApi = async (
  storeId: StoreId,
  billingItemId: BillingItemId,
  billingItem: RestUpdateBillingItem,
): Promise<RestBillingItem> => {
  return api.put(`stores/${storeId}/billingItems/${billingItemId}`, billingItem);
};

export const deleteBillingItemApi = async (
  storeId: StoreId,
  billingItemId: BillingItemId,
): Promise<void> => {
  return api.delete(`stores/${storeId}/billingItems/${billingItemId}`);
};

export const getActiveFeaturesApi = async (
  storeId: StoreId,
): Promise<Features[]> => {
  return api.get(`stores/${storeId}/features`);
};

